import { useMemo, useState } from "react";
import { useLocation } from "react-router";

import { useMetricHelp } from "@/components/Analytics/useMetricHelp";
import { AskNobiButton } from "@/components/AskNobi/AskNobiButton";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ChartLegend } from "@/components/Charts/ChartLegend";
import { HistogramChart } from "@/components/Charts/HistogramChart";
import { dataColors } from "@/components/Charts/theme";
import { NoData } from "@/components/NoData/NoData";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { PageGrid } from "@/components/PageGrid/PageGrid";
import { addPortfolioProjectFilterQueryParams } from "@/components/ProjectFilter/portfolioProjectFilterQueryParams";
import { Stat } from "@/components/Stat/Stat";
import { Inline } from "@/design-system/Inline";
import { Select } from "@/design-system/Select";
import { Spinner } from "@/design-system/Spinner";
import { Stack } from "@/design-system/Stack";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { useSlug } from "@/hooks/useSlug";
import { useAnalyticsScope } from "@/modules/analytics/AnalyticsScopeContext";
import { AnalyticsCard } from "@/modules/analytics/components/AnalyticsCard";
import { AnalyticsPageContent } from "@/modules/analytics/components/AnalyticsPageContent";
import { AnalyticsSection } from "@/modules/analytics/components/AnalyticsSection";
import { BarChart } from "@/modules/analytics/components/BarChart";
import { LineChart } from "@/modules/analytics/components/LineChart";
import { sentimentColors, SentimentDistributionChart } from "@/modules/analytics/components/SentimentDistributionChart";
import { SentimentTopicWordCloud } from "@/modules/analytics/components/SentimentTopicWordCloud";
import { ANALYTICS_AUDIENCE_SCOPE_COLORS } from "@/modules/analytics/constants";
import type {
  CommunityFeedAnalyticsResponseBucket,
  CommunityFeedSentimentAnalytics,
} from "@/queries/analytics/community-feed-reporting-types";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";
import type { NonEmptyArray } from "@/types/utility-types";

import type { CommunityFeedAnalyticsDateRangeOption } from "../community-feed/CommunityFeedAnalyticsPage";

interface Props {
  /** `focused` shows the core story; `all` adds every exploration section. Defaults to `all`. */
  mode?: "focused" | "all";
  sentiment: CommunityFeedSentimentAnalytics | undefined;
  isLoading: boolean;
  isFetching: boolean;
  selectedDateRange: CommunityFeedAnalyticsDateRangeOption;
  dateRangeOptions: NonEmptyArray<CommunityFeedAnalyticsDateRangeOption>;
  onDateRangeChange: (option: CommunityFeedAnalyticsDateRangeOption) => void;
}

/**
 * Renders community-feed sentiment analytics in either a concise `focused` view or the complete `all` view.
 * The complete view remains the default for existing consumers that do not specify a mode.
 */
export function CommunityFeedSentimentAnalyticsContent({
  mode = "all",
  sentiment,
  isLoading,
  isFetching,
  selectedDateRange,
  dateRangeOptions,
  onDateRangeChange,
}: Props): React.ReactNode {
  const { t } = useTranslation();
  const analyticsScope = useAnalyticsScope();
  const location = useLocation();
  const slug = useSlug({ optional: true });
  const projectId = useProjectId();
  const portfolioRoute = (path: string) => addPortfolioProjectFilterQueryParams(path, location.search);
  let content: React.ReactNode;

  if (isLoading || !sentiment) {
    content = (
      <section className="flex flex-1 items-center justify-center py-space-xl" aria-busy="true">
        <Spinner size="xl" />
      </section>
    );
  } else if (sentiment.hasSufficientData) {
    content = <SentimentContent mode={mode} sentiment={sentiment} />;
  } else {
    content = (
      <section className="flex min-h-scroll-area-standard items-center justify-center">
        <NoData
          noDataMessage={t("page.analytics-community-feed.sentiment.insufficient-page", {
            count: sentiment.minimumSampleSize,
          })}
        />
      </section>
    );
  }

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={
        mode === "focused"
          ? t("page.analytics-community-feed.sentiment.focus.title")
          : t("page.analytics-community-feed.sentiment.title")
      }
      title={
        mode === "focused"
          ? t("page.analytics-community-feed.sentiment.focus.title")
          : t("page.analytics-community-feed.sentiment.title")
      }
      breadcrumbs={
        <Breadcrumbs
          pages={[
            analyticsScope.type === "portfolio"
              ? { name: t("page.portfolio.title"), to: portfolioRoute(routes.portfolio.overview()) }
              : { name: t("page.analytics-story.title"), to: routes.analytics.overview({ slug: slug! }) },
            {
              name: t("page.analytics-community-feed.breadcrumb"),
              to:
                analyticsScope.type === "portfolio"
                  ? portfolioRoute(routes.portfolio.analyticsCommunityFeed())
                  : routes.analytics.communityFeedReporting({ slug: slug! }),
            },
            { name: t("page.analytics-community-feed.sentiment.breadcrumb") },
          ]}
        />
      }
      actions={
        <AskNobiButton
          defaultFilters={{ projectIds: analyticsScope.type === "portfolio" ? analyticsScope.projectIds : [projectId] }}
          context={{ scope: analyticsScope.type, entity: "analytics", page: "detail" }}
          suggestions={[
            t("page.analytics-community-feed.sentiment.nobi-suggestion.1"),
            t("page.analytics-community-feed.sentiment.nobi-suggestion.2"),
          ]}
        />
      }
      header={
        <Inline gap="sm" align="center" wrap={false}>
          <span className="text-body-emphasis">{t("page.analytics-details.time-range.label")}</span>
          <Select
            items={dateRangeOptions}
            selected={selectedDateRange}
            onChange={onDateRangeChange}
            renderOption={(option) => option.name}
            keySelector={(option) => option.value}
          />
          {analyticsScope.type === "portfolio" && analyticsScope.filterAction}
          {isFetching && !isLoading && <Spinner size="sm" />}
        </Inline>
      }
    >
      <AnalyticsPageContent>
        {mode === "focused" && (
          <p className="max-w-measure-wide text-body text-text-secondary">
            {t("page.analytics-community-feed.sentiment.focus.description")}
          </p>
        )}
        {content}
      </AnalyticsPageContent>
    </DocumentPage>
  );
}

function SentimentContent({
  mode,
  sentiment,
}: {
  mode: "focused" | "all";
  sentiment: CommunityFeedSentimentAnalytics;
}): React.ReactNode {
  const { t } = useTranslation();
  const help = useMetricHelp();
  const [selectedTopic, setSelectedTopic] = useState<string>();
  const [previousTopics, setPreviousTopics] = useState(sentiment.topics);
  const chartLabels = useSentimentChartLabels();

  if (sentiment.topics !== previousTopics) {
    setPreviousTopics(sentiment.topics);

    if (selectedTopic !== undefined && !sentiment.topics.some((topic) => topic.topic === selectedTopic)) {
      setSelectedTopic(undefined);
    }
  }

  const averageSentimentData = useMemo(
    () => [
      {
        label: t("page.analytics-community-feed.sentiment.average-score"),
        color: dataColors.primary,
        dataPoints: sentiment.monthly
          .filter((point) => point.averageScore !== undefined)
          .map((point) => ({ x: point.x, y: point.averageScore!, isPending: point.isCurrentMonth })),
      },
    ],
    [sentiment.monthly, t],
  );
  const answeredNegativeData = useMemo(
    () =>
      sentiment.monthly.map((point) => ({
        x: point.x,
        y: point.answeredNegativePosts,
        isPending: point.isCurrentMonth,
      })),
    [sentiment.monthly],
  );
  const unansweredNegativeData = useMemo(
    () =>
      sentiment.monthly.map((point) => ({
        x: point.x,
        y: point.unansweredNegativePosts,
        isPending: point.isCurrentMonth,
      })),
    [sentiment.monthly],
  );
  const responseTimeData = useMemo(
    () =>
      sentiment.responseTimeDistribution.map((point) => ({
        category: point.x as CommunityFeedAnalyticsResponseBucket,
        label: t(`page.analytics-community-feed.response-bucket.${point.x as CommunityFeedAnalyticsResponseBucket}`),
        value: point.y,
      })),
    [sentiment.responseTimeDistribution, t],
  );
  const audienceData = useMemo(
    () =>
      sentiment.audienceScopes
        .map((scope, index) => ({
          label:
            scope.type === "projectOnly"
              ? t("page.analytics-community-feed.connections.project-only")
              : (scope.projectConnection?.name ?? t("page.analytics-community-feed.connections.project-only")),
          color: ANALYTICS_AUDIENCE_SCOPE_COLORS[index % ANALYTICS_AUDIENCE_SCOPE_COLORS.length],
          dataPoints: scope.dataPoints
            .filter((point) => point.averageScore !== undefined)
            .map((point) => ({ x: point.x, y: point.averageScore!, isPending: point.isCurrentMonth })),
        }))
        .filter((series) => series.dataPoints.length > 0),
    [sentiment.audienceScopes, t],
  );

  return (
    <PageGrid.Row>
      <PageGrid.Column size="100%">
        <AnalyticsSection
          isFullWidth
          title={
            mode === "focused"
              ? t("page.analytics-community-feed.sentiment.focus.signals")
              : t("page.analytics-community-feed.sentiment.overview-section")
          }
        >
          <AnalyticsCard.Root>
            <AnalyticsCard.Header
              title={t("page.analytics-community-feed.sentiment.overview-title")}
              tooltip={t("page.analytics-community-feed.sentiment.method-tooltip", {
                count: sentiment.minimumSampleSize,
              })}
            />
            <section className="flex flex-col gap-space-md @xs:flex-row @xs:flex-wrap">
              <Stat
                label={t("page.analytics-community-feed.sentiment.analyzed-posts")}
                value={sentiment.analyzedResidentPosts}
                renderFooter={() =>
                  t("page.analytics-community-feed.sentiment.unscored-posts", {
                    count: sentiment.unscoredResidentPosts,
                  })
                }
              />
              <Stat
                label={t("page.analytics-community-feed.sentiment.average-score")}
                value={sentiment.averageScore}
                renderValue={(value) => (value === undefined ? "-" : `${value.toFixed(1)}/10`)}
              />
              <Stat
                label={t("page.analytics-community-feed.sentiment.positive-share")}
                value={sentiment.overall.positivePercentage}
                renderValue={(value) => (value === undefined ? "-" : `${value}%`)}
              />
              <Stat
                label={t("page.analytics-community-feed.sentiment.negative-share")}
                value={sentiment.overall.negativePercentage}
                renderValue={(value) => (value === undefined ? "-" : `${value}%`)}
              />
              <Stat
                label={t("page.analytics-community-feed.sentiment.negative-response-rate")}
                value={sentiment.responsiveness.responseRatePercentage}
                renderValue={(value) => (value === undefined ? "-" : `${value}%`)}
                renderFooter={() =>
                  t("page.analytics-community-feed.sentiment.negative-response-detail", {
                    answered: sentiment.responsiveness.answeredNegativePosts,
                    total: sentiment.responsiveness.negativePosts,
                  })
                }
              />
            </section>
          </AnalyticsCard.Root>
        </AnalyticsSection>
      </PageGrid.Column>

      <PageGrid.Column size="100%">
        <AnalyticsSection
          title={t("page.analytics-community-feed.sentiment.trend-section")}
          description={t("page.analytics-community-feed.sentiment.trend-description")}
        >
          <AnalyticsCard.Root variant="sentimentGraph">
            <AnalyticsCard.Header
              title={t("page.analytics-community-feed.sentiment.distribution-title")}
              tooltip={help("page.analytics-community-feed.sentiment.distribution-title.tooltip")}
            />
            <figure className="flex min-h-scroll-area-compact flex-1 flex-col">
              <SentimentDistributionChart data={sentiment.monthly} labels={chartLabels} />
              <SentimentLegend />
            </figure>
          </AnalyticsCard.Root>
          <AnalyticsCard.Root variant="sentimentGraph">
            <AnalyticsCard.Header
              title={t("page.analytics-community-feed.sentiment.average-title")}
              tooltip={help("page.analytics-community-feed.sentiment.average-title.tooltip")}
            />
            <figure className="flex min-h-scroll-area-compact flex-1 flex-col">
              <LineChart
                data={averageSentimentData}
                xAxisType="date"
                maxY={10}
                yLabel={t("page.analytics-community-feed.sentiment.axis-score")}
                hoverLabel={(point) => (
                  <Stack gap="xs">
                    <span>{point.relatedPoints[0].dataPoint.y.toFixed(1)}/10</span>
                    {point.relatedPoints[0].dataPoint.isPending && (
                      <span>{t("page.analytics-details.engagement.chart.tooltip.data-collection-in-progress")}</span>
                    )}
                  </Stack>
                )}
              />
              <ChartLegend items={[{ label: averageSentimentData[0].label, color: dataColors.primary }]} />
            </figure>
          </AnalyticsCard.Root>
        </AnalyticsSection>
      </PageGrid.Column>

      {mode === "all" && sentiment.topics.length > 0 && (
        <PageGrid.Column size="100%">
          <AnalyticsSection
            title={t("page.analytics-community-feed.sentiment.topic-section")}
            description={t("page.analytics-community-feed.sentiment.topic-description", {
              count: sentiment.minimumSampleSize,
            })}
            isFullWidth
          >
            <AnalyticsCard.Root variant="content">
              <AnalyticsCard.Header
                title={t("page.analytics-community-feed.sentiment.topic-cloud-title")}
                tooltip={help("page.analytics-community-feed.sentiment.topic-cloud-title.tooltip")}
              />
              <SentimentTopicWordCloud
                topics={sentiment.topics}
                selectedTopic={selectedTopic}
                onSelectedTopicChange={setSelectedTopic}
              />
            </AnalyticsCard.Root>
          </AnalyticsSection>
        </PageGrid.Column>
      )}

      <PageGrid.Column size="100%">
        <AnalyticsSection
          title={t("page.analytics-community-feed.sentiment.response-section")}
          description={t("page.analytics-community-feed.sentiment.response-description")}
        >
          <AnalyticsCard.Root variant="sentimentGraph">
            <AnalyticsCard.Header
              title={t("page.analytics-community-feed.sentiment.negative-response-title")}
              tooltip={help("page.analytics-community-feed.sentiment.negative-response-title.tooltip")}
            />
            <figure className="flex min-h-scroll-area-compact flex-1 flex-col">
              <BarChart
                dataPrimary={{ data: answeredNegativeData }}
                dataSecondary={{ data: unansweredNegativeData, color: sentimentColors.negative }}
                xAxisType="date"
                yTickMode="integer"
                yLabel={t("page.analytics-community-feed.axis.posts")}
                hoverLabel={(bar) => (
                  <Stack gap="xs">
                    <span>
                      {t("page.analytics-community-feed.sentiment.answered")}: {bar.primary?.y ?? 0}
                    </span>
                    <span>
                      {t("page.analytics-community-feed.sentiment.unanswered")}: {bar.secondary?.y ?? 0}
                    </span>
                    {(bar.primary?.isPending || bar.secondary?.isPending) && (
                      <span>{t("page.analytics-details.engagement.chart.tooltip.data-collection-in-progress")}</span>
                    )}
                  </Stack>
                )}
              />
              <ChartLegend
                items={[
                  { label: t("page.analytics-community-feed.sentiment.answered"), color: dataColors.primary },
                  { label: t("page.analytics-community-feed.sentiment.unanswered"), color: sentimentColors.negative },
                ]}
              />
            </figure>
          </AnalyticsCard.Root>
          <AnalyticsCard.Root variant="content">
            <AnalyticsCard.Header
              title={t("page.analytics-community-feed.sentiment.negative-response-time")}
              tooltip={help("page.analytics-community-feed.sentiment.negative-response-time.tooltip")}
            />
            <HistogramChart data={responseTimeData} />
          </AnalyticsCard.Root>
        </AnalyticsSection>
      </PageGrid.Column>

      {mode === "all" && audienceData.length > 1 && (
        <PageGrid.Column size="100%">
          <AnalyticsSection
            title={t("page.analytics-community-feed.sentiment.connections-section")}
            description={t("page.analytics-community-feed.sentiment.connections-description")}
            isFullWidth
          >
            <AnalyticsCard.Root variant="sentimentGraph">
              <AnalyticsCard.Header
                title={t("page.analytics-community-feed.sentiment.connections-title")}
                tooltip={help("page.analytics-community-feed.sentiment.connections-title.tooltip")}
              />
              <figure className="flex min-h-scroll-area-compact flex-1 flex-col">
                <LineChart
                  data={audienceData}
                  xAxisType="date"
                  maxY={10}
                  yLabel={t("page.analytics-community-feed.sentiment.axis-score")}
                  hoverLabel={(point) => (
                    <ul className="flex flex-col gap-space-xs">
                      {point.relatedPoints.map((relatedPoint) => (
                        <li key={relatedPoint.label}>
                          {relatedPoint.label}: {relatedPoint.dataPoint.y.toFixed(1)}/10
                        </li>
                      ))}
                      {point.relatedPoints.some((relatedPoint) => relatedPoint.dataPoint.isPending) && (
                        <li>{t("page.analytics-details.engagement.chart.tooltip.data-collection-in-progress")}</li>
                      )}
                    </ul>
                  )}
                />
                <ChartLegend items={audienceData.map((series) => ({ label: series.label, color: series.color }))} />
              </figure>
            </AnalyticsCard.Root>
          </AnalyticsSection>
        </PageGrid.Column>
      )}
    </PageGrid.Row>
  );
}

function useSentimentChartLabels() {
  const { t } = useTranslation();

  return {
    analyzedPosts: t("page.analytics-community-feed.sentiment.analyzed-posts"),
    insufficientData: t("page.analytics-community-feed.sentiment.insufficient-month"),
    negative: t("page.analytics-community-feed.sentiment.negative"),
    neutral: t("page.analytics-community-feed.sentiment.neutral"),
    positive: t("page.analytics-community-feed.sentiment.positive"),
    currentPeriod: t("page.analytics-details.engagement.chart.tooltip.data-collection-in-progress"),
    yAxis: t("page.analytics-community-feed.sentiment.axis-share"),
  };
}

function SentimentLegend(): React.ReactNode {
  const { t } = useTranslation();

  return (
    <ChartLegend
      items={[
        { label: t("page.analytics-community-feed.sentiment.positive"), color: sentimentColors.positive },
        { label: t("page.analytics-community-feed.sentiment.neutral"), color: sentimentColors.neutral },
        { label: t("page.analytics-community-feed.sentiment.negative"), color: sentimentColors.negative },
        {
          label: t("page.analytics-details.engagement.chart.legend.current-period"),
          color: dataColors.primary,
          pattern: "striped",
        },
      ]}
    />
  );
}
